import React, { useContext, useEffect, useLayoutEffect, useMemo, useState, useRef } from 'react';
import { Box, Typography, Paper, ListItem, ListItemText, Stack, Skeleton, IconButton, Menu, MenuItem } from '@mui/material';
import { VariableSizeList as List } from 'react-window';
import { useNavigate } from 'react-router-dom';
import { NotificationContext } from 'contexts';
import { getTranslation } from 'common';
import { useTranslations, useOrganizations } from 'hooks';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import ArrowOutwardIcon from '@mui/icons-material/ArrowOutward';
import SettingsIcon from '@mui/icons-material/Settings';
import { NotificationContextType } from 'contexts/notificationContext';
import { formatDate } from 'common/utilities';
import NotificationsOrganizationsList from './NotificationsOrganizationsList';
import { Check, NotificationsActive } from '@mui/icons-material';

type RowProps = {
    index: number;
    style: Object;
};

type NotificationsListProps = {
    compact?: boolean;
    onClose?: () => void;
};

const NotificationsList: React.FC<NotificationsListProps> = ({ compact = false, onClose }) => {
    const [loading, setLoading] = useState<boolean>(false);
    const [filterOrgId, setFilterOrgId] = useState<number | null>(null);
    const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
    const translations = useTranslations()
    const [organizations] = useOrganizations();
    const navigate = useNavigate();
    const HEADER_HEIGHT = 65;
    // ponytail: measured width of the list container. AutoSizer under-reported
    // (~220 vs the Popover's 300) — measuring clientWidth via ResizeObserver
    // on a ref gives the real value.
    const listContainerRef = useRef<HTMLDivElement | null>(null);
    const [listWidth, setListWidth] = useState<number>(0);
    useLayoutEffect(() => {
        const el = listContainerRef.current;
        if (!el) return;
        const update = () => {
            const w = el.clientWidth;
            if (w > 0) setListWidth(w);
        };
        update();
        // ponytail: on remount (e.g. after switching org with 0 notifications)
        // the first read can be 0 because the box has no width yet. Retry on
        // the next frame so we capture the real value.
        const raf = requestAnimationFrame(update);
        const ro = new ResizeObserver(update);
        ro.observe(el);
        return () => {
            cancelAnimationFrame(raf);
            ro.disconnect();
        };
    }, []);
    const { actions: { readNotification, readAllNotifications, readAllNotificationsGlobal }, state: { notificationList } } = useContext<NotificationContextType>(NotificationContext)

    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        setMenuAnchor(event.currentTarget);
    };

    const handleMenuClose = () => {
        setMenuAnchor(null);
    };
    //which id notif should have no link?
    const noIdLinkNotifs = [5, 10, 11, 14, 16, 30, 35, 36, 41, 43, 48, 50]

    const readAllNotifs = () => {
        handleMenuClose();
        if (filterOrgId === null) {
            readAllNotificationsGlobal();
            return;
        }
        readAllNotifications({ idorg: filterOrgId });
    };

    const goToSettings = () => {
        handleMenuClose();
        navigate('notifications/settings');
    };

    const goToNotificationsPage = () => {
        handleMenuClose();
        onClose?.();
        navigate('notifications/list');
    };

    const listRef = useRef<List>(null);
    const allNotifications = useMemo(() => {
        if (filterOrgId === null) return notificationList;
        return notificationList?.filter(n => n.idorganization === filterOrgId);
    }, [notificationList, filterOrgId]);

    const filteredOrgName = useMemo(() => {
        if (filterOrgId === null) return;
        const org = organizations.find(o => o.id === filterOrgId);
        return org?.name ?? "";
    }, [filterOrgId, organizations]);

    //definitely not ai generated function to calculate the height of each item since i could not apply padding to the list item for some reason with this "compact" prop;
    //if u manage to make it work, please just make this function getItemsize ()=>{return 52} and remove calculations
    //p.s. I've checked the numbers, this is decently fine at the moment
    const getItemSize = (index: number): number => {
        if (!compact) return 52;

        const notification = allNotifications[index];
        if (!notification) return 52;

        const charsPerLine = 25;
        const lines = Math.ceil(notification.notificationtext.length / charsPerLine);
        return 70 + (lines - 1) * 20;
    };

    const Row: React.FC<RowProps> = React.memo(({ index, style }) => {
        const notification = allNotifications[index];
        if (!notification) return null;

        return (
            <ListItem
                id={`notif-${String(notification.id)}`}
                data-cy="notification-item"
                divider
                style={{ ...style, cursor: notification.readed ? "default" : "pointer", }}
                onClick={() => {
                    if (!notification.readed) {
                        readNotification({ id: notification.id });
                    }
                }}
                disabled={notification.readed}
            >
                <ListItemText
                    primary={
                        <Typography
                            sx={{ fontWeight: notification.readed ? 'normal' : 'bold' }}
                        >
                            {notification.notificationtext}
                        </Typography>
                    }
                    secondary={
                        <Typography
                        >
                            {formatDate(notification.notificationtime)}
                        </Typography>
                    }
                />
                {/* Il link e' un path relativo `/dashboard/...` (GetLinkUrlForNotification):
                    le notifiche SLA puntano a `/dashboard/myactivities/task/{idprocesstoken}`,
                    rotta esistente che riusa MyActivities (decisione 2, sez. 14.2). */}
                {notification.linkurl && (
                    <IconButton
                        size="small"
                        onClick={(e) => {
                            e.stopPropagation();
                            navigate(notification.linkurl);
                        }}
                    >
                        <ArrowOutwardIcon fontSize="small" />
                    </IconButton>
                )}
            </ListItem>
        );
    });

    useEffect(() => {
        setLoading(true)
    }, [])


    return (
        <Stack
            sx={{
                flex: 1,
                maxHeight: `100%`,
                borderRadius: "15px",
            }}
        >
            <Box display="flex" gap={2} flexDirection="column">
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 2, ml: 2 }}>
                    <Typography variant="h4" sx={{ flex: 1 }}>
                        {getTranslation(translations, "menu.left.notifications")}
                    </Typography>
                    <IconButton onClick={handleMenuOpen} size="small" sx={{ mr: 1 }}>
                        <MoreHorizIcon />
                    </IconButton>
                    <Menu
                        anchorEl={menuAnchor}
                        open={Boolean(menuAnchor)}
                        onClose={handleMenuClose}
                    >
                        <MenuItem onClick={readAllNotifs}>
                            <Check sx={{ color: "primary.main" }} />  {getTranslation(translations, "generic.notifications.read_all")}
                        </MenuItem>
                        <MenuItem onClick={goToSettings}>
                            <SettingsIcon sx={{ color: "primary.main" }} />  {getTranslation(translations, "menu.left.settings")}
                        </MenuItem>
                        <MenuItem onClick={goToNotificationsPage}>
                            <NotificationsActive sx={{ color: "primary.main" }} />  {getTranslation(translations, "menu.left.notifications")}
                        </MenuItem>
                    </Menu>
                </Box>
                <NotificationsOrganizationsList
                    value={filterOrgId}
                    onChange={setFilterOrgId}
                />
            </Box>
            {/* <FormControlLabel
                control={
                    <Checkbox
                        checked={false}
                        onChange={readAllNotifs}
                    />
                }
                label={getTranslation(translations, "generic.notifications.read_all")}
            /> */}
            <Box
                component={Paper}
                sx={{
                    maxHeight: `calc(100% - ${HEADER_HEIGHT}px})`,
                    height: `calc(100% - ${HEADER_HEIGHT}px)`,
                    width: '100%',
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    pb: 2,
                    position: "relative",
                    paddingTop: 6
                }}>
                {allNotifications?.length === 0 ?
                    <Box height="100%" display={"flex"} alignItems="center" justifyContent="center">
                        <Typography textAlign={"center"}>
                            {filterOrgId !== null && filteredOrgName
                                ? `${getTranslation(translations, "notifications.no_notifications")} for ${filteredOrgName}`
                                : getTranslation(translations, "notifications.no_notifications")}
                        </Typography>
                    </Box>
                    :
                    <>
                        {loading &&
                            <Box sx={{ px: 2, position: "absolute", inset: 0 }}>
                                {Array.from(Array(10).keys()).map((k) => <Skeleton variant="rectangular" width="100%" height={50} sx={{ borderRadius: 2, my: "5px" }} key={`skeleton-${k}`} />)}
                            </Box>
                        }

                        <Box
                            ref={listContainerRef}
                            sx={{ height: `calc(100% - ${HEADER_HEIGHT}px)`, minHeight: 500, width: '100%' }}
                        >
                            {listWidth > 0 && (
                                <List
                                    ref={listRef}
                                    onItemsRendered={() => setLoading(false)}
                                    height={typeof window !== 'undefined' ? window.innerHeight : 600}
                                    width={listWidth}
                                    itemSize={getItemSize}
                                    itemCount={allNotifications?.length}
                                >
                                    {Row}
                                </List>
                            )}
                        </Box>
                    </>
                }
            </Box>
        </Stack>
    );
};

export default NotificationsList